/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {formatDate} from '@angular/common';
import {HttpEventType} from '@angular/common/http';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ThemePalette} from '@angular/material/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {BUSINESS_PLAN, DISMISS_MSG, PROPULSION_COLUMN_HEADERS, TVP} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {VIOLATION_TABS} from '../../../core/enums/propulsion-violation.enum';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {StateService} from '../../../core/state/state.service';
import {PropulsionPortfolioLoadResult} from '../../../models/propulsion-portfolio-load-result.model';
import {PropulsionPortfolioLoadViolation} from '../../../models/propulsion-portfolio-load-violation.model';
import {PropulsionPortfolioLoad} from '../../../models/propulsion-portfolio-load.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {User} from '../../../models/user.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionPortfolioLoadService} from '../../../services/propulsion-portfolio-load.service';

enum RESULT_TABS {
  PROP_NO_VEH = 'Prop No Veh',
  VEH_NO_PROP = 'Veh No Prop',
  PORTFOLIO = 'Portfolio',
  NEW_ADDS = 'New Adds',
  UPDATE = 'Update Records',
  NO_CHANGE = 'No Change Records',
  DUPLICATES = 'Duplicate Records'
}

@Component({
    selector: 'app-propulsion-portfolio-load',
    templateUrl: './propulsion-portfolio-load.component.html',
    styleUrls: ['./propulsion-portfolio-load.component.scss'],
    standalone: false
})
export class PropulsionPortfolioLoadComponent implements OnInit {

  @ViewChild('portfolioTabGroup') portfolioTabGroup: MatTabGroup;
  @ViewChild('violationTabGroup') violationTabGroup: MatTabGroup;
  @ViewChild('resultTabGroup') resultTabGroup: MatTabGroup;

  displayedData: PropulsionPortfolioLoad[];
  rowsToDelete: PropulsionPortfolioLoad[] = [];
  portfolioData: Map<string, PropulsionPortfolioLoad[]>;
  allViolations: Map<string, PropulsionPortfolioLoadViolation> = new Map<string, PropulsionPortfolioLoadViolation>();
  allLoadResults: Map<string, PropulsionPortfolioLoadResult> = new Map<string, PropulsionPortfolioLoadResult>();
  violations: PropulsionPortfolioLoadViolation;
  propPortLoadResults: PropulsionPortfolioLoadResult;

  activeFcorgTab: MatTab;
  portfolioTabs: string[];
  propulsionColumnHeaders: ColumnHeader[] = PROPULSION_COLUMN_HEADERS;
  displayedColDef: ColDef[] = this.propulsionColumnHeaders.map(header => {
    return {field: header.name, headerName: header.label, filter: AutocompleteFilterComponent, lockVisible: true};
  });

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    selectAll: "filtered",
    enableClickSelection: true,
    enableSelectionWithoutKeys: true
  };

  sheets: SelectableOptions = new SelectableOptions();

  hasViolations: boolean;
  hasData: boolean;
  hasResults: boolean;
  isInitialLoad: boolean;
  isLoadButtonDisabled: boolean;
  isAddUpdateTabDisabled: boolean;
  showSpinner: boolean;

  pageSize: number;
  fcorgTabIndex: number;
  violationTabIndex: number;
  resultTabIndex: number;

  CURRENT_PROP_REPORT = 'Current Propulsion Portfolio';
  selectedPortType: string;
  currentPropPortfolio: PropulsionPortfolioLoad[];
  BUSINESS_PLAN = BUSINESS_PLAN;
  TVP = TVP;

  file: File;
  user: User;
  worksheetExportRequest: WorksheetExportRequest;
  exportButtonColor: ThemePalette = 'accent';

  constructor(public readonly propPortfolioService: PropulsionPortfolioLoadService,
              public readonly excelService: ExcelExportService,
              public snackBar: FabMessageService,
              private readonly dialog: MatDialog,
              private readonly userService: StateService) {
  }

  ngOnInit(): void {
    this.selectedPortType = BUSINESS_PLAN;
    this.user = this.userService.user;
    this.pageSize = 100;
    this.setDefaults();
  }

  /**
   * Event handler for when an upload event is emitted. Receives the propulsion portfolio load
   * result data in the event. Displays the results to the user, whether any validation error occurs
   * or any records were successfully updated/added/deleted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.setDefaults();
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.fcorgTabIndex = 0;
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: true
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
          if (this.sheets.selectedOptions.length > 0) {
            this.getPortfolioData();
          }
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    );
  }

  /**
   * Get the propulsion portfolio template data from the Excel file that was uploaded
   */
  getPortfolioData(): void {
    this.showSpinner = true;
    this.hasViolations = false;
    this.propPortfolioService.transform(this.file, this.sheets.selectedOptions).subscribe(
      (e: any) => {
        // Get propulsion portfolio load data from the uploaded Excel file
        if (e.type === HttpEventType.Response) {
          this.portfolioData = e.body;
        }
      },
      (error) => {
        if (error.status === 0) {
          this.snackBar.displayError('Error occurred. Please re-upload file.', DISMISS_MSG);
        } else {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
        this.portfolioTabs = Object.keys(this.portfolioData);
        this.hasData = this.portfolioData !== undefined && this.portfolioTabs.length > 0;
        if (this.hasData) {
          this.fcorgTabIndex = 0;
          this.displayedData = this.portfolioData[this.portfolioTabs[this.fcorgTabIndex]];
          this.showSpinner = false;
          this.rowsToDelete = [];
          this.setExportRequest();
          this.isLoadButtonDisabled = false;
          this.setExportButtonColor();
        }
      });
  }


  /**
   * Get the current vehicle portfolio by port type and export to excel
   * @param portType portfolio type
   */
  exportCurrentPortfolioByPortType(portType: string): void {
    this.showSpinner = true;
    this.propPortfolioService.getCurrentPortfolioByPortType(portType).subscribe(
      (data) => {
        this.currentPropPortfolio = data;
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        const exportRequest = this.getExportRequestForCurrentPortfolio();
        this.excelService.transformToExcel(exportRequest).subscribe(
          (file: any) => {
            this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
          },
          () => {
            this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
            this.showSpinner = false;
          }, () => {
            this.showSpinner = false;
          });
        this.showSpinner = false;
      });
  }

  /**
   * Helper function used to get the export request for the current portfolio
   */
  getExportRequestForCurrentPortfolio(): WorksheetExportRequest {
    const excelData = new Map<string, PropulsionPortfolioLoad[]>();
    excelData[this.CURRENT_PROP_REPORT] = this.currentPropPortfolio;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    return this.excelService.createExportRequestBody('CurrentPropPort_' + currDate,
      excelData,
      [[this.CURRENT_PROP_REPORT, this.propulsionColumnHeaders]]);
  }

  /**
   * Validate the propulsion portfolio load template, and perform a load
   * if there are no violations
   */
  validate(loadTemplate: Map<string, PropulsionPortfolioLoad[]>): void {
    // Do validation pre-check on the data
    // Check to ensure there is only 1 FCORG per sheet
    let hasUniqueFcorg = true;
    Object.keys(loadTemplate).forEach((key) => {
      const fcorgVals = [...new Set(loadTemplate[key].map(row => row.fcOrg))];
      if (fcorgVals.length > 1) {
        hasUniqueFcorg = false;
      }
    });
    if (!hasUniqueFcorg) {
      this.snackBar.displayError('FCORG in each sheet must be same.', DISMISS_MSG);
      // Disable load button if pre-check fails
      this.isLoadButtonDisabled = true;
    } else {
      this.showSpinner = true;
      // Validate the propulsion load template
      this.propPortfolioService.validate(loadTemplate).subscribe(
        (allViolations: Map<string, PropulsionPortfolioLoadViolation>) => {
          // Get list of violations from the load template
          this.allViolations = allViolations;
          this.fcorgTabIndex = 0;
          this.violations = allViolations[this.portfolioTabs[this.fcorgTabIndex]];
        },
        (error) => {
          this.snackBar.displayError(error, DISMISS_MSG);
          this.showSpinner = false;
        },
        () => {
          this.showSpinner = false;
          this.violationTabIndex = 0;
          const firstNonEmptyValidationList: string = this.getFirstNonEmptyValidationList();
          this.hasViolations = this.allViolations !== undefined && firstNonEmptyValidationList !== undefined;
          if (this.hasViolations) {
            this.snackBar.displayError('There were violations found in the load template', DISMISS_MSG);
            this.isLoadButtonDisabled = true;
            this.setExportButtonColor();
            this.displayedData = this.violations[firstNonEmptyValidationList];
            this.showSpinner = false;
            this.rowsToDelete = [];
            this.setExportRequest();
          } else {
            // If there are no violations, load the template
            this.snackBar.displayMessage('No violations, performing load.', DISMISS_MSG);
            this.load(loadTemplate);
          }
        });
    }
  }

  /**
   * Load the propulsion portfolio template
   */
  load(loadTemplate: Map<string, PropulsionPortfolioLoad[]>): void {
    this.showSpinner = true;
    this.propPortfolioService.load(loadTemplate, this.isInitialLoad, this.user.gmid).subscribe(
      (allResults: Map<string, PropulsionPortfolioLoadResult>) => {
        // Get list of results from doing the load
        this.allLoadResults = allResults;
        this.fcorgTabIndex = 0;
        this.propPortLoadResults = allResults[this.portfolioTabs[this.fcorgTabIndex]];
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.resultTabIndex = 0;
        const firstNonEmptyResultList: string = this.getFirstNonEmptyResultList();
        this.hasResults = firstNonEmptyResultList !== undefined;
        this.isLoadButtonDisabled = true;
        this.isAddUpdateTabDisabled = this.isInitialLoad;
        this.setExportButtonColor();
        if (this.hasResults) {
          this.displayedData = this.propPortLoadResults[firstNonEmptyResultList];
          this.rowsToDelete = [];
          this.showSpinner = false;
          this.setExportRequest();
          this.snackBar.displaySuccess('The Propulsion Portfolio was successfully loaded', DISMISS_MSG);
        } else {
          this.snackBar.displayError('No data was loaded from the portfolio template', DISMISS_MSG);
          this.showSpinner = false;
        }
      });
  }

  /**
   * Delete the selected rows from the portfolio
   */
  delete(): void {
    this.showSpinner = true;
    // Create list of prop portfolio ids to delete
    const idList = this.rowsToDelete
    .filter(row => row.propPortfolioId !== null && row.propPortfolioId !== undefined)
    .map(row => row.propPortfolioId);

    // Delete the records from the portfolio
    this.propPortfolioService.delete(idList).subscribe({
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.snackBar.displaySuccess('Successfully deleted the selected portfolio records.', DISMISS_MSG);
        // Remove the deleted records from the portfolio data
        Object.keys(this.portfolioData).forEach((key) => {
          this.portfolioData[key] = this.portfolioData[key].filter(row => !idList.includes(row.propPortfolioId));
        });
        // Update the data source and display data
        const tab = this.portfolioTabGroup._allTabs.get(this.portfolioTabGroup.selectedIndex);
        this.displayedData = [...this.portfolioData[tab.textLabel]];
        this.exportDeletedRecords(this.rowsToDelete);
        this.rowsToDelete = [];
        this.showSpinner = false;
      }
    });
  }

  /**
   * Gets the name of the first non-empty validation list
   * @return string - name of the first non-empty validation list, undefined if not found
   */
  getFirstNonEmptyValidationList(): string {
    return Object.keys(this.violations).find(key => this.violations[key].length > 0);
  }

  /**
   * Gets the name of the first non-empty result list
   * @return string - name of the first non-empty result list, undefined if not found
   */
  getFirstNonEmptyResultList(): string {
    return Object.keys(this.propPortLoadResults).find(key => this.propPortLoadResults[key].length > 0);
  }

  /**
   * Update the datasource of the table data based off the FCORG tab change
   * @param $event The event that is emitted when a tab was changed
   */
  fcorgTabChange($event): void {
    this.activeFcorgTab = $event.tab;
    if (!this.hasViolations && !this.hasResults) {
      this.displayedData = this.portfolioData[this.activeFcorgTab.textLabel];
    } else if (!this.hasViolations && this.hasResults) {
      // If there are result tabs, reset the result tabs back to the first tab
      this.propPortLoadResults = this.allLoadResults[this.activeFcorgTab.textLabel];
      const getFirstNonEmptyResultList = this.getFirstNonEmptyResultList();
      this.displayedData = getFirstNonEmptyResultList !== undefined ?
        this.propPortLoadResults[getFirstNonEmptyResultList] : [];
      // Set timeout triggers new angular cycle to reset the tab index
      setTimeout(() => {
        this.resultTabGroup.selectedIndex = 0;
      });
    } else {
      // If there are violations, reset the violation tab group back to the first tab
      this.violations = this.allViolations[this.activeFcorgTab.textLabel];
      const firstNonEmptyValidList: string = this.getFirstNonEmptyValidationList();
      this.displayedData = firstNonEmptyValidList !== undefined ? this.violations[firstNonEmptyValidList] : [];
      // Set timeout triggers new angular cycle to reset the tab index
      setTimeout(() => {
        this.violationTabGroup.selectedIndex = 0;
      });
    }
    this.rowsToDelete = [];
  }

  /**
   * Update the datasource of the Violation table data based off the tab change
   * @param $event The event that is emitted when a tab was changed
   */
  violationTabChange($event): void {
    if ($event !== undefined && $event.index !== -1) {
      switch ($event.tab.textLabel) {
        case VIOLATION_TABS.DUPLICATE_ID:
          this.displayedData = this.violations.duplicateIdList;
          break;
        case VIOLATION_TABS.DUPLICATE_ENG_DTL:
          this.displayedData = this.violations.duplicateEngList;
          break;
        case VIOLATION_TABS.DUPLICATE_TRAN_DTL:
          this.displayedData = this.violations.duplicateTranList;
          break;
        case VIOLATION_TABS.DUPLICATE_BAT:
          this.displayedData = this.violations.duplicateBatList;
          break;
        case VIOLATION_TABS.DUPLICATE_ELEC:
          this.displayedData = this.violations.duplicateElecList;
          break;
        case VIOLATION_TABS.DUPLICATE_PRI_DRV:
          this.displayedData = this.violations.duplicatePriDrvList;
          break;
        case VIOLATION_TABS.DUPLICATE_SEC_DRV:
          this.displayedData = this.violations.duplicateSecDrvList;
          break;
        case VIOLATION_TABS.INVALID_PORTTYPE:
          this.displayedData = this.violations.invalidPortTypeList;
          break;
        case VIOLATION_TABS.DATE_VIOLATION:
          this.displayedData = this.violations.dateViolations;
          break;
        default:
          break;
      }
      this.rowsToDelete = [];
    }
  }

  /**
   * Update the datasource of the table data based off the result tab changes
   * @param $event The event that is emitted when a tab was changed
   */
  resultTabChange($event): void {
    switch ($event.tab.textLabel) {
      case RESULT_TABS.PROP_NO_VEH:
        this.displayedData = this.propPortLoadResults.propNoVehList;
        break;
      case RESULT_TABS.VEH_NO_PROP:
        this.displayedData = this.propPortLoadResults.vehNoPropList;
        break;
      case RESULT_TABS.PORTFOLIO:
        this.displayedData = this.propPortLoadResults.portfolio;
        break;
      case RESULT_TABS.NEW_ADDS:
        this.displayedData = this.propPortLoadResults.addRecords;
        break;
      case RESULT_TABS.UPDATE:
        this.displayedData = this.propPortLoadResults.updateRecords;
        break;
      case RESULT_TABS.NO_CHANGE:
        this.displayedData = this.propPortLoadResults.noChangeRecords;
        break;
      case RESULT_TABS.DUPLICATES:
        this.displayedData = this.propPortLoadResults.duplicateRecords;
        break;
      default:
        break;
    }
    this.rowsToDelete = [];
  }

  /**
   * Event handler for selecting a propulion portfolio record
   * @param event the clicked row
   */
  onSelect(event) {
    if (event.node.selected) {
      this.rowsToDelete.push(event.data);
    } else {
      this.rowsToDelete = this.rowsToDelete.filter(element => element != event.data);
    }
  }

  /**
   * Set boolean values used to display components back to their defaults
   */
  setDefaults(): void {
    this.portfolioTabs = [];
    this.fcorgTabIndex = 0;
    this.violationTabIndex = 0;
    this.resultTabIndex = 0;
    this.hasData = false;
    this.isLoadButtonDisabled = true;
    this.isAddUpdateTabDisabled = false;
    this.setExportButtonColor();
    this.hasViolations = false;
    this.hasResults = false;
    this.isInitialLoad = false;
  }

  /**
   * Helper function used to set the export request
   */
  setExportRequest(): void {
    // Set timeout so that TabGroups do not return as undefined
    setTimeout(() => {
      const currDate = new Date().toISOString();
      if (!this.hasViolations && !this.hasResults) {
        // Export the portfolios
        const tabGroup: [string, ColumnHeader[]][] = this.portfolioTabGroup._allTabs.toArray().map(tab => {
          return [tab.textLabel, this.propulsionColumnHeaders];
        });
        this.worksheetExportRequest = this.excelService.createExportRequestBody('PropulsionPortfolio_' + currDate,
          this.portfolioData,
          tabGroup);
      } else if (!this.hasViolations && this.hasResults) {
        // Export the results
        this.setResultExportRequest();
        this.exportToExcel();
      } else if (this.hasViolations) {
        // Export the violations
        this.setViolationExportRequest();
      }
    });
  }

  /**
   * Helper function to create the Export request for the violation sheets
   */
  setViolationExportRequest(): void {
    const currDate = new Date().toISOString();
    const columnHeaderMap = new Map<string, ColumnHeader[]>();
    const fcorg = 'fcOrg';
    // Set the columns used for each violation sheet
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_ID] = this.propulsionColumnHeaders;
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_ENG_DTL] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'engRpo', 'engCyl', 'injection', 'boost'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_TRAN_DTL] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'tranRpo', 'tranName', 'tranSpeed'].includes(header.name));
    // Special edge case for the Bat Violation sheet (business wants the batRpo col to display before cellFam, batDesc)
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'batRpo'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT] = [...columnHeaderMap[VIOLATION_TABS.DUPLICATE_BAT],
      ...this.propulsionColumnHeaders.filter(header => ['cellFam', 'batDesc'].includes(header.name))];
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_ELEC] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'elecRpo', 'elecFam', 'elecDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_PRI_DRV] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'priDrvRpo', 'priDrvDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.DUPLICATE_SEC_DRV] = this.propulsionColumnHeaders.filter(
      header => [fcorg, 'secDrvRpo', 'secDrvDesc'].includes(header.name));
    columnHeaderMap[VIOLATION_TABS.INVALID_PORTTYPE] = this.propulsionColumnHeaders;
    columnHeaderMap[VIOLATION_TABS.DATE_VIOLATION] = this.propulsionColumnHeaders;
    const violationTabs: [string, ColumnHeader[]][] = this.violationTabGroup._allTabs.toArray().map(tab => {
      return [tab.textLabel, columnHeaderMap[tab.textLabel]];
    });
    const data = new Map<string, any>();
    data[VIOLATION_TABS.DUPLICATE_ID] = [];
    data[VIOLATION_TABS.DUPLICATE_ENG_DTL] = [];
    data[VIOLATION_TABS.DUPLICATE_TRAN_DTL] = [];
    data[VIOLATION_TABS.DUPLICATE_BAT] = [];
    data[VIOLATION_TABS.DUPLICATE_ELEC] = [];
    data[VIOLATION_TABS.DUPLICATE_PRI_DRV] = [];
    data[VIOLATION_TABS.DUPLICATE_SEC_DRV] = [];
    data[VIOLATION_TABS.INVALID_PORTTYPE] = [];
    data[VIOLATION_TABS.DATE_VIOLATION] = [];
    // Consolidate the data for the violation sheets for each FCORG into one violation sheet
    this.portfolioTabGroup._allTabs.toArray().forEach(tab => {
      const violations = this.allViolations[tab.textLabel];
      data[VIOLATION_TABS.DUPLICATE_ID] = [...data[VIOLATION_TABS.DUPLICATE_ID], ...violations.duplicateIdList];
      data[VIOLATION_TABS.DUPLICATE_ENG_DTL] = [...data[VIOLATION_TABS.DUPLICATE_ENG_DTL],
        ...violations.duplicateEngList];
      data[VIOLATION_TABS.DUPLICATE_TRAN_DTL] = [...data[VIOLATION_TABS.DUPLICATE_TRAN_DTL],
        ...violations.duplicateTranList];
      data[VIOLATION_TABS.DUPLICATE_BAT] = [...data[VIOLATION_TABS.DUPLICATE_BAT], ...violations.duplicateBatList];
      data[VIOLATION_TABS.DUPLICATE_ELEC] = [...data[VIOLATION_TABS.DUPLICATE_ELEC], ...violations.duplicateElecList];
      data[VIOLATION_TABS.DUPLICATE_PRI_DRV] = [...data[VIOLATION_TABS.DUPLICATE_PRI_DRV],
        ...violations.duplicatePriDrvList];
      data[VIOLATION_TABS.DUPLICATE_SEC_DRV] = [...data[VIOLATION_TABS.DUPLICATE_SEC_DRV],
        ...violations.duplicateSecDrvList];
      data[VIOLATION_TABS.INVALID_PORTTYPE] = [...data[VIOLATION_TABS.INVALID_PORTTYPE],
        ...violations.invalidPortTypeList];
      data[VIOLATION_TABS.DATE_VIOLATION] = [...data[VIOLATION_TABS.DATE_VIOLATION],
        ...violations.dateViolations];
    });
    // Send the request
    this.worksheetExportRequest = this.excelService.createExportRequestBody('ViolationsPropPortLoad_' + currDate,
      data,
      violationTabs);
  }

  /**
   * Helper function to create the Export request for the result sheets after a load
   */
  setResultExportRequest(): void {
    const currDate = new Date().toISOString();
    // Create list of sheet names of that will be on the Result export excel file
    let sheetList: string[] = [RESULT_TABS.PROP_NO_VEH, RESULT_TABS.VEH_NO_PROP,
      RESULT_TABS.NEW_ADDS, RESULT_TABS.UPDATE, RESULT_TABS.NO_CHANGE, RESULT_TABS.DUPLICATES];
    if (this.isAddUpdateTabDisabled) {
      sheetList = [RESULT_TABS.PROP_NO_VEH, RESULT_TABS.VEH_NO_PROP, RESULT_TABS.DUPLICATES];
    }
    this.portfolioTabGroup._allTabs.toArray().forEach(tab => {
      sheetList.push(tab.textLabel);
    });
    const data = new Map<string, any>();
    // Initialize the result sheets that would have consolidated data
    data[RESULT_TABS.PROP_NO_VEH] = [];
    data[RESULT_TABS.VEH_NO_PROP] = [];
    data[RESULT_TABS.NEW_ADDS] = [];
    data[RESULT_TABS.UPDATE] = [];
    data[RESULT_TABS.NO_CHANGE] = [];
    data[RESULT_TABS.DUPLICATES] = [];
    // Create the list of sheet name : column header pairs to send as part of the request body
    const resultTabs: [string, ColumnHeader[]][] = sheetList.map(sheetName => {
      return [sheetName, this.propulsionColumnHeaders];
    });
    // Create the data object that contains what data goes on each sheet
    this.portfolioTabGroup._allTabs.toArray().forEach(tab => {
      const results = this.allLoadResults[tab.textLabel];
      // Set data for the FCORG sheets to its current portfolio data
      data[tab.textLabel] = results.portfolio;
      // Consolidate result data for the other sheets
      data[RESULT_TABS.PROP_NO_VEH] = [...data[RESULT_TABS.PROP_NO_VEH], ...results.propNoVehList];
      data[RESULT_TABS.VEH_NO_PROP] = [...data[RESULT_TABS.VEH_NO_PROP], ...results.vehNoPropList];
      data[RESULT_TABS.NEW_ADDS] = [...data[RESULT_TABS.NEW_ADDS], ...results.addRecords];
      data[RESULT_TABS.UPDATE] = [...data[RESULT_TABS.UPDATE], ...results.updateRecords];
      data[RESULT_TABS.NO_CHANGE] = [...data[RESULT_TABS.NO_CHANGE], ...results.noChangeRecords];
      data[RESULT_TABS.DUPLICATES] = [...data[RESULT_TABS.DUPLICATES], ...results.duplicateRecords];
    });
    this.worksheetExportRequest = this.excelService.createExportRequestBody('ResultsPropPortLoad_' + currDate,
      data,
      resultTabs);
  }

  /**
   * Exports an Excel file containing a list of deleted records
   * @param deletedRecords - list of deleted records to export
   */
  exportDeletedRecords(deletedRecords: PropulsionPortfolioLoad[]): void {
    const sheetName = 'Deleted Records';
    const sheetList: [string, ColumnHeader[]][] = [[sheetName, this.propulsionColumnHeaders]];
    const data = new Map<string, any>();
    data[sheetName] = deletedRecords;
    const currDate = new Date().toISOString();
    const deleteExportRequest = this.excelService.createExportRequestBody('DeletedPropRecords_' + currDate,
      data,
      sheetList);
    this.excelService.transformToExcel(deleteExportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, deleteExportRequest.fileName);
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Private method to set the color for the export button
   */
  private setExportButtonColor(): void {
    if (this.isLoadButtonDisabled) {
      this.exportButtonColor = 'primary';
    } else {
      this.exportButtonColor = 'accent';
    }
  }

  /**
   * Export table data to excel
   */
  exportToExcel(): void {
    this.showSpinner = true;
    if (this.worksheetExportRequest === undefined) {
      this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
      this.showSpinner = false;
    } else {
      this.excelService.transformToExcel(this.worksheetExportRequest).subscribe(
        (file: any) => {
          this.excelService.exportToExcelFromServer(file, this.worksheetExportRequest.fileName);
        },
        () => {
          this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
          this.showSpinner = false;
        }, () => {
          this.showSpinner = false;
        });
    }
  }
}
